import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Crossmatch } from '../interfaces/crossmatch';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CrossmatchService {
  private apiUrl = environment.apiUrl + 'crossmatches';

  //Shared crossmatches
  private excludedCombinationsSubject = new BehaviorSubject<Crossmatch[]>([]);
  private negativeCrossmatchesSubject = new BehaviorSubject<Crossmatch[]>([]);
  private positiveCrossmatchesSubject = new BehaviorSubject<Crossmatch[]>([]);
  excludedCombinations$ = this.excludedCombinationsSubject.asObservable();
  negativeCrossmatches$ = this.negativeCrossmatchesSubject.asObservable();
  positiveCrossmatches$ = this.positiveCrossmatchesSubject.asObservable();

  constructor(private http: HttpClient) {}

  createCrossmatch(crossmatchData: Crossmatch): Observable<Crossmatch> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Crossmatch>(this.apiUrl, crossmatchData, { headers }).pipe(
      catchError((error) => {
        console.error('Error creating crossmatch:', error);
        return throwError(() => new Error('Failed to create crossmatch'));
      })
    );
  }

  deleteCrossmatch(crossmatchId: string): Observable<Crossmatch> {
    console.log('Deleting crossmatch:', crossmatchId);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.delete<Crossmatch>(`${this.apiUrl}/${crossmatchId}`, { headers }).pipe(
      catchError((error) => {
        console.error('Error deleting crossmatch:', error);
        return throwError(() => new Error('Failed to delete crossmatch'));
      })
    );
  }

  updateCrossmatch(crossmatchId: string, updates: Partial<Crossmatch>): Observable<Crossmatch> {
    console.log('Updating crossmatch:', crossmatchId, updates);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.patch<Crossmatch>(`${this.apiUrl}/${crossmatchId}`, updates, { headers }).pipe(
      catchError((error) => {
        console.error('Error updating crossmatch:', error);
        return throwError(() => new Error('Failed to update crossmatch'));
      })
    );
  }

  getAllCrossmatches(): Observable<Crossmatch[]> {
    return this.http.get<Crossmatch[]>(this.apiUrl).pipe(
      catchError((error) => {
        console.error('Error fetching crossmatches:', error);
        return throwError(() => new Error('Failed to fetch crossmatches'));
      })
    );
  }

  //Shared crossmatch functions
  setExcludedCombinations(combinations: Crossmatch[]) {
    this.excludedCombinationsSubject.next(combinations);
  }

  addExcludedCombination(combination: Crossmatch) {
    const currentCombinations = this.excludedCombinationsSubject.value;
    this.excludedCombinationsSubject.next([combination, ...currentCombinations]);
  }

  removeExcludedCombination(id: string) {
    const currentCombinations = this.excludedCombinationsSubject.value;
    this.excludedCombinationsSubject.next(currentCombinations.filter((item) => item.id !== id));
  }

  setNegativeCrossmatches(crossmatches: Crossmatch[]) {
    this.negativeCrossmatchesSubject.next(crossmatches);
  }

  setPositiveCrossmatches(crossmatches: Crossmatch[]) {
    this.positiveCrossmatchesSubject.next(crossmatches);
  }

  addNegativeCrossmatch(crossmatch: Crossmatch) {
    const currentCrossmatches = this.negativeCrossmatchesSubject.value;
    this.negativeCrossmatchesSubject.next([crossmatch, ...currentCrossmatches]);
  }

  addPositiveCrossmatch(crossmatch: Crossmatch) {
    const currentCrossmatches = this.positiveCrossmatchesSubject.value;
    this.positiveCrossmatchesSubject.next([crossmatch, ...currentCrossmatches]);
  }

  removeNegativeCrossmatch(id: string) {
    const currentCrossmatches = this.negativeCrossmatchesSubject.value;
    this.negativeCrossmatchesSubject.next(currentCrossmatches.filter((item) => item.id !== id));
  }

  removePositiveCrossmatch(id: string) {
    const currentCrossmatches = this.positiveCrossmatchesSubject.value;
    this.positiveCrossmatchesSubject.next(currentCrossmatches.filter((item) => item.id !== id));
  }
}
